import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ChangeDetectionStrategy,
  HostBinding,
  signal,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';

// ─── Types — alignés sur Checkbox ────────────────────────────────────────────

export type ToggleSize  = 'small' | 'medium' | 'large';
export type ToggleColor =
  | 'amarante' | 'moonstone' | 'rose' | 'mint'
  | 'semantic-red' | 'semantic-yellow' | 'semantic-green';
export type ToggleState = 'default' | 'disabled';

// ─── ToggleComponent ──────────────────────────────────────────────────────────

@Component({
  selector:    'app-toggle',
  standalone:  true,
  imports:     [CommonModule],
  templateUrl: './toggle.component.html',
  styleUrls:   ['./toggle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide:     NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleComponent),
      multi:       true,
    },
    {
      provide:     NG_VALIDATORS,
      useExisting: forwardRef(() => ToggleComponent),
      multi:       true,
    },
  ],
})
export class ToggleComponent implements ControlValueAccessor, Validator {

  // ── Apparence ────────────────────────────────────────────────────────────────
  @Input() size:  ToggleSize  = 'medium';
  @Input() color: ToggleColor = 'amarante';
  @Input() state: ToggleState = 'default';

  // ── Contenu ──────────────────────────────────────────────────────────────────
  @Input() label = '';

  // ── Valeur (one-way binding sans CVA) ────────────────────────────────────────
  @Input() set checked(val: boolean) { this._checked.set(!!val); }

  // ── Validation ───────────────────────────────────────────────────────────────
  @Input() required     = false;
  @Input() errorMessage = 'Ce champ est obligatoire';

  // ── Comportement ─────────────────────────────────────────────────────────────
  @Input() fullWidth = false;

  @HostBinding('style.width')
  get hostWidth(): string { return this.fullWidth ? '100%' : ''; }

  // ── Output ───────────────────────────────────────────────────────────────────
  @Output() changed = new EventEmitter<boolean>();

  // ── State interne ────────────────────────────────────────────────────────────
  readonly _checked   = signal(false);
  readonly isTouched  = signal(false);

  private readonly _cdr = inject(ChangeDetectorRef);

  // ── CVA ──────────────────────────────────────────────────────────────────────
  private _onChange:  (v: boolean) => void = () => {};
  private _onTouched: ()           => void = () => {};

  writeValue(val: boolean): void {
    this._checked.set(!!val);
  }
  registerOnChange(fn: any):  void { this._onChange = fn; }
  registerOnTouched(fn: any): void { this._onTouched = fn; }
  /**
   * `state` n'est pas un signal : sans `markForCheck`, un `enable()`/`disable()`
   * posé sur le contrôle après le premier rendu ne repeindrait pas l'interrupteur.
   */
  setDisabledState(disabled: boolean): void {
    this.state = disabled ? 'disabled' : 'default';
    this._cdr.markForCheck();
  }

  // ── Validator ─────────────────────────────────────────────────────────────────
  validate(_: AbstractControl): ValidationErrors | null {
    if (!this.required) return null;
    return this._checked() ? null : { required: true };
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  get isDisabled(): boolean { return this.state === 'disabled'; }

  get showError(): boolean {
    return this.required && this.isTouched() && !this._checked();
  }

  get wrapperClasses(): Record<string, boolean> {
    return {
      'toggle':                  true,
      [`toggle--${this.size}`]:  true,
      [`toggle--${this.color}`]: true,
      'toggle--checked':         this._checked(),
      'toggle--disabled':        this.isDisabled,
      'toggle--error':           this.showError,
      'toggle--full-width':      this.fullWidth,
    };
  }

  onToggle(): void {
    if (this.isDisabled) return;
    this.isTouched.set(true);
    this._onTouched();
    this._checked.update(v => !v);
    this._onChange(this._checked());
    this.changed.emit(this._checked());
  }
}
